import { useState } from 'react'

import { useFacilitySearch } from '@/hooks/useFacilitySearch'
import type { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HiArrowLeft } from 'react-icons/hi'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'use-debounce'

import {
  Box,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
} from '@chakra-ui/react'

import { Drawer } from '@/components/overlays'
import {
  Button,
  FacilityOptionLabel,
  FormInputSelectControl,
} from '@/components/ui'
import { Action, FacilityType } from '@/graphql/generated/schemas'
import { removeNullValues } from '@/redux/incidents/utils'
import { Filter, MultiFilter } from '@/redux/types/filters'
import {
  selectIncidentRulesActionsFilter,
  selectIncidentRulesFacilityIdsFilter,
  selectIncidentRulesStatusFilter,
  setIncidentRulesActionsFilter,
  setIncidentRulesFacilityIdsFilter,
  setIncidentRulesStatusFilter,
} from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'
import {
  createFacilityAddressFilter,
  getIncidentFacilityOptionsWithAddress,
} from '@/utils/forms/optionBuilders'
import {
  SEARCH_DEBOUNCE_TIME,
  getFacilityNoOptionsMessage,
} from '@/utils/forms/validation'

import { getTranslatedActionText } from '../../utils/ruleRows'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

interface RulesFilterFormInputsI {
  facilityIds: MultiFilter | null
  actions: MultiFilter<Action> | null
  status: Filter<boolean> | null
}

const getActionOptions = (t: TFunction) => [
  { label: getTranslatedActionText(t, Action.Mute), value: Action.Mute },
  {
    label: getTranslatedActionText(t, Action.SendEmail),
    value: Action.SendEmail,
  },
  {
    label: getTranslatedActionText(t, Action.EnableAiOperator),
    value: Action.EnableAiOperator,
  },
  {
    label: getTranslatedActionText(t, Action.SetPriority),
    value: Action.SetPriority,
  },
]

const getStatusOptions = () => [
  { label: 'On', value: true },
  { label: 'Off', value: false },
]

export const RulesFilterDrawer = ({ isOpen, onClose }: IProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [facilityName, setFacilityName] = useState<string>('')
  const [debouncedFacilityName] = useDebounce(
    facilityName,
    SEARCH_DEBOUNCE_TIME
  )

  const {
    facilitiesData,
    isFacilitiesLoading,
    isError: isFacilitiesError,
  } = useFacilitySearch({
    searchQuery: debouncedFacilityName,
    filter: { type: FacilityType.Normal },
  })

  const { control, handleSubmit, setValue } = useForm({
    reValidateMode: 'onChange',
  })

  const facilityIdsFilterValue = useSelector(
    selectIncidentRulesFacilityIdsFilter
  )
  const actionsFilterValue = useSelector(selectIncidentRulesActionsFilter)
  const statusFilterValue = useSelector(selectIncidentRulesStatusFilter)

  const facilityOptions = getIncidentFacilityOptionsWithAddress(
    facilitiesData,
    true,
    debouncedFacilityName
  )
  const actionOptions = getActionOptions(t)
  const statusOptions = getStatusOptions()

  const onClearFilters = () => {
    setValue('facilityIds', null)
    setValue('actions', null)
    setValue('status', null)

    mixpanel.track('Button Press: Rules Drawer - Clear Filters')
  }

  const onSubmit = (values: RulesFilterFormInputsI) => {
    const selectedFacilityIds = values?.facilityIds
    const selectedActions = values?.actions
    const selectedStatus = values?.status

    dispatch(setIncidentRulesFacilityIdsFilter(selectedFacilityIds || null))
    dispatch(setIncidentRulesActionsFilter(selectedActions || null))
    dispatch(setIncidentRulesStatusFilter(selectedStatus || null))
    onClose()

    mixpanel.track('Incident Rules View: Filters Applied', {
      filters: removeNullValues({
        facilityIds: selectedFacilityIds,
        actions: selectedActions,
        status: selectedStatus,
      }),
    })
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement='right' size='sm'>
      <DrawerOverlay data-testid='RulesFilterDrawer:overlay' />
      <DrawerContent>
        <DrawerHeader
          alignItems='center'
          borderBottomWidth='1px'
          borderColor='#D5DCE4'
          d='flex'
          minH='65px'
          px='5'
          py='0'
        >
          <HStack spacing='3'>
            <IconButton
              aria-label='Back button'
              borderRadius='4px'
              data-testid='incidentRulesPage_filterDrawer_backButton'
              h='30px'
              icon={<HiArrowLeft size={18} />}
              minWidth='30px'
              onClick={onClose}
              variant='ghost'
              w='30px'
            />
            <Box fontSize='20px' fontWeight='bold' letterSpacing='-0.53px'>
              Filter Rules
            </Box>
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <Box mb='3' mt='6'>
            <FormInputSelectControl
              closeMenuOnSelect={false}
              control={control}
              dataTestId='incidentRulesPage_filterDrawer_facility'
              defaultValue={facilityIdsFilterValue || null}
              // Filters by facility name, short name, and address. Without
              // filterOption, React Select defaults to label-only search.
              filterOption={createFacilityAddressFilter}
              formatOptionLabel={(option, meta) => (
                <FacilityOptionLabel
                  isValueContext={meta?.context === 'value'}
                  option={option}
                />
              )}
              id='facilityIds'
              isClearable
              isLoading={isFacilitiesLoading}
              isMulti
              isSearchable
              label='Facility'
              noOptionsMessage={({ inputValue }) =>
                getFacilityNoOptionsMessage(
                  inputValue,
                  isFacilitiesError,
                  t('common:facility_search_error')
                )
              }
              onInputChange={setFacilityName}
              options={facilityOptions}
              placeholder='Select Facilities'
            />
          </Box>
          <Box mb='3'>
            <FormInputSelectControl
              closeMenuOnSelect={false}
              control={control}
              dataTestId='incidentRulesPage_filterDrawer_action'
              defaultValue={actionsFilterValue || null}
              id='actions'
              isClearable
              isMulti
              isSearchable={false}
              label='Action'
              options={actionOptions}
              placeholder='Select Actions'
            />
          </Box>
          <Box mb='3'>
            <FormInputSelectControl
              control={control}
              dataTestId='incidentRulesPage_filterDrawer_status'
              defaultValue={statusFilterValue || null}
              id='status'
              isClearable
              isSearchable={false}
              label='Status'
              options={statusOptions}
              placeholder='Select Status'
            />
          </Box>
        </DrawerBody>
        <DrawerFooter borderColor='#D5DCE4' borderTopWidth='1px'>
          <HStack spacing='5' width='full'>
            <Button
              data-testid='incidentRulesPage_filterDrawer_clearButton'
              isFullWidth
              onClick={onClearFilters}
              variant='secondary'
            >
              Clear All
            </Button>
            <Button
              data-testid='incidentRulesPage_filterDrawer_applyButton'
              isFullWidth
              onClick={handleSubmit(onSubmit)}
            >
              Apply Filters
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
